import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, UserRound } from "lucide-react";

/**
 * CommandCenterProfilePage (forge-20260612-003) — the checklist's
 * "User Profile" item lands here. Registration collects phone as optional
 * while the Step-1 checklist requires it; this is the member's
 * self-service completion surface (phone only — name is locked to the
 * verified identity, address belongs to the agreement flow).
 */
const CommandCenterProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/profile", { phone });
      await refreshUser();
      toast.success("Phone number saved — profile item complete");
      navigate("/command-center");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Could not save your phone number");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Your Profile" subtitle="Complete your account details">
      <Card className="max-w-xl bg-white border-slate-200" data-testid="profile-complete-card">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <UserRound size={20} className="text-slate-700" /> Account details
          </CardTitle>
          <CardDescription className="text-slate-600">
            A phone number is required to complete onboarding — we use it for
            verification and compliance notices only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-2 text-sm text-slate-700">
            <p>Legal name: <span className="font-semibold">{user?.full_name || "—"}</span></p>
            <p>Email: <span className="font-semibold">{user?.email || "—"}</span></p>
          </div>
          <div>
            <Label className="text-slate-900">Phone number</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              className="mt-1 bg-white text-slate-900 border-slate-300"
              data-testid="profile-phone-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving || !phone.trim()}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="profile-phone-save">
              {saving ? "Saving..." : "Save phone number"}
            </Button>
            <Link to="/command-center" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Back to your checklist
            </Link>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default CommandCenterProfilePage;
